import {ShieldCheckIcon, ShieldIcon, ShieldSlashIcon} from '@primer/octicons-react'
import {RepositoryAndAlertPicker} from './RepositoryAndAlertPicker'
import type {BaseAlert} from './AlertPicker'
import {
  codeScanningOrgRepositoryListPath,
  codeScanningAlertPickerPath,
  codeScanningAlertTrackingLinksPath,
} from '@github-ui/paths'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import type {AlertsQueryRequest} from '../hooks/use-alerts-query'
import {useCallback} from 'react'

export type CodeScanningAlertStatus = 'OPEN' | 'FIXED' | 'DISMISSED'

export interface CodeScanningAlert extends BaseAlert {
  state: CodeScanningAlertStatus
}

export function getAlertKey(alert: CodeScanningAlert): string {
  return `code_scanning/${alert.repository.nameWithOwner}/${alert.number}`
}

function getLeadingVisual(alert: CodeScanningAlert) {
  switch (alert.state) {
    case 'FIXED':
      return <ShieldCheckIcon className="fgColor-done" />
    case 'DISMISSED':
      return <ShieldSlashIcon className="fgColor-closed" />
    default:
      return <ShieldIcon className="fgColor-open" />
  }
}

// If the filter looks like "https://:hostname/:owner/:repo/security/code-scanning/:number" then parse out those values.
// Otherwise treat it as a query against the current owner/repo.
function getAlertsQueryRequest(currentOwner: string, currentRepo: string, filter: string): AlertsQueryRequest {
  const codeScanningAlertUrlPattern = new RegExp(
    `https://${window.location.hostname}/([^/]+)/([^/]+)/security/code-scanning/(\\d+)`,
  )
  filter = filter.trim()
  const match = filter.match(codeScanningAlertUrlPattern)
  if (match) {
    const [, owner, repo, numberString] = match
    const number = parseInt(numberString ?? '', 10)
    return {
      path: codeScanningAlertPickerPath({owner: owner ?? '', repo: repo ?? ''}),
      number,
    }
  } else {
    return {
      path: codeScanningAlertPickerPath({owner: currentOwner, repo: currentRepo}),
      query: filter,
    }
  }
}

interface CodeScanningAlertPickerProps {
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  selectedAlerts: CodeScanningAlert[]
  onSelection: () => void
  onError: (errors: Error[]) => void
  initialOwner: string
  initialRepo: string
  issueId: number
}

// Currently CodeScanningAlertPicker is the public interface for this package,
// but in the future we plan to add support for other security alert types.
// When that happens, there will be another component wrapping this which allows
// selecting the alert type and switching between sub-components, similar to how
// RepositoryAndAlertPicker wraps AlertPicker.
export function CodeScanningAlertPicker(props: CodeScanningAlertPickerProps) {
  const {selectedAlerts, onSelection, onError, issueId, ...rest} = props

  const handleAlertSelectionChanged = useCallback(
    async (newAlerts: CodeScanningAlert[]) => {
      const selectedAlertKeys = new Set(selectedAlerts.map(getAlertKey))
      const newAlertKeys = new Set(newAlerts.map(getAlertKey))

      const alertsToAdd = Array.from(newAlerts).filter(alert => !selectedAlertKeys.has(getAlertKey(alert)))
      const alertsToRemove = Array.from(selectedAlerts).filter(alert => !newAlertKeys.has(getAlertKey(alert)))

      const mutationPromises: Array<Promise<Error | null>> = []

      for (const alert of alertsToAdd) {
        const [owner, repo] = alert.repository.nameWithOwner.split('/')
        const path = codeScanningAlertTrackingLinksPath({
          owner: owner ?? '',
          repo: repo ?? '',
          alertNumber: alert.number,
        })
        mutationPromises.push(
          (async () => {
            const response = await reactFetchJSON(path, {method: 'PATCH', body: {issue_id: issueId}})
            if (!response.ok) {
              return new Error(
                `Failed to link alert ${alert.number} in ${alert.repository.nameWithOwner}: status ${response.status}`,
              )
            }
            return null
          })(),
        )
      }

      for (const alert of alertsToRemove) {
        const [owner, repo] = alert.repository.nameWithOwner.split('/')
        const path = codeScanningAlertTrackingLinksPath({
          owner: owner ?? '',
          repo: repo ?? '',
          alertNumber: alert.number,
        })
        mutationPromises.push(
          (async () => {
            const response = await reactFetchJSON(path, {method: 'PATCH', body: {issue_id: 0}})
            if (!response.ok) {
              return new Error(
                `Failed to unlink alert ${alert.number} in ${alert.repository.nameWithOwner}: status ${response.status}`,
              )
            }
            return null
          })(),
        )
      }

      const errors = await Promise.all(mutationPromises)
      onError(errors.flatMap(error => error || []))

      onSelection()
    },
    [onSelection, onError, selectedAlerts, issueId],
  )

  return (
    <RepositoryAndAlertPicker<CodeScanningAlert>
      {...rest}
      selectedAlerts={selectedAlerts}
      onAlertSelectionChanged={handleAlertSelectionChanged}
      getAlertsQueryRequest={getAlertsQueryRequest}
      getAlertRepositoriesPath={owner => codeScanningOrgRepositoryListPath({org: owner})}
      getAlertKey={getAlertKey}
      getLeadingVisual={getLeadingVisual}
    />
  )
}
